import { LitElement, html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { INDETERMINATE_VALUE, SpinnerRadius, SpinnerWidth } from '../types';

export class GzProgressSpinnerWebComponent extends LitElement {
  // see https://lit-element.polymer-project.org/guide/properties
  @property({ type: Boolean, attribute: true, reflect: true })
  indeterminate = true;

  @property({ type: Number, reflect: true })
  value;

  @property({ type: String, attribute: true, reflect: true })
  spinnerWidth = SpinnerWidth.MEDIUM;

  @property({ type: Number, attribute: true, reflect: true })
  customSpinnerWidthValue: number; // in pixels

  @state()
  radius: SpinnerRadius = SpinnerRadius.MEDIUM;

  @state()
  stroke = 2;

  @state()
  spinnerSize = new Map<SpinnerWidth, SpinnerRadius>([
    [SpinnerWidth.SMALL, SpinnerRadius.SMALL],
    [SpinnerWidth.MEDIUM, SpinnerRadius.MEDIUM],
    [SpinnerWidth.LARGE, SpinnerRadius.LARGE],
  ]);

  @state()
  private viewBox = this.radius * 2 + this.stroke;

  @state()
  private circumference = this.radius * 2 * Math.PI;

  @state()
  strokeDashoffset = `${this.circumference}px`;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  private setValue(value) {
    if (value === undefined || value === null) {
      return;
    }
    const offset = (this.circumference * (100 - value)) / 100;
    this.strokeDashoffset = `${offset}px`;
  }

  private setCircumference() {
    this.circumference = this.radius * 2 * Math.PI;
  }
  private setViewBox() {
    this.viewBox = this.radius * 2 + this.stroke;
  }

  private updateSpinner(val) {
    this.setCircumference();
    this.setViewBox();
    this.setValue(val);
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.indeterminate) {
      this.radius = this.spinnerSize.get(this.spinnerWidth) ?? this.customSpinnerWidthValue ?? INDETERMINATE_VALUE;
      this.updateSpinner(INDETERMINATE_VALUE);
    }
  }

  attributeChangedCallback(name, oldVal, newVal) {
    super.attributeChangedCallback(name, oldVal, newVal);

    if (name === 'value' && newVal) {
      this.indeterminate = false;
      this.updateComplete.then(() => {
        this.updateSpinner(newVal);
      });
    }
    if (name === 'spinnerwidth' && newVal) {
      this.updateComplete.then(() => {
        this.radius = this.spinnerSize.get(newVal) ?? this.customSpinnerWidthValue ?? this.value;
        this.updateSpinner(this.value);
      });
    }
  }

  render() {
    return html`
      <svg
        viewBox="0 0 ${this.viewBox} ${this.viewBox}"
        width="${this.radius}"
        height="${this.radius}"
        class="progress-ring"
      >
        <circle class="progress-ring--circle-bar" fill="transparent" r="${this.radius}" cx="50%" cy="50%" />
        <circle
          class="progress-ring--circle"
          stroke-dashoffset="${this.strokeDashoffset}"
          stroke-dasharray="${this.circumference}"
          fill="transparent"
          r="${this.radius}"
          cx="50%"
          cy="50%"
        />
      </svg>
    `;
  }
}
